import { Component, onMount, createSignal, onCleanup, For, createMemo, Show } from 'solid-js';
import StatsCard from '../components/ui/StatsCard';
import DataTable, { Column } from '../components/tables/DataTable';
import SearchInput from '../components/ui/SearchInput';
import CountryFlag from '../components/ui/CountryFlag';
import NetworkLogo from '../components/ui/NetworkLogo';
import LineChart from '../components/charts/LineChart';
import { conversionsApi, statsApi } from '../services/api';
import { config } from '../config/environment';
import { setDocumentTitle } from '../utils/document';
import { useSocketEvent } from '../services/socket';
import { getTodayGMT, getDateRangeGMT, formatGMTTime, formatWIBTime, formatGMTDateTime, isTodayGMT } from '../utils/timezone';
import { getConversionsColumns } from '../utils/conversionsColumns';
import { TbClick, TbCheckbox, TbPercentage, TbCalendar, TbMail, TbPhone, TbWorld, TbCoins, TbX, TbMinus, TbPlus, TbChartLine } from 'solid-icons/tb';
import { OcFeedtrophy2 } from 'solid-icons/oc';
import PlatformIcon, { getPlatformColor } from '../components/ui/PlatformIcon';
import type { Conversion } from '../types';
import moment from 'moment';
import './NewRowAnimation.css';

interface ConversionWithAnimation extends Conversion {
  _isNew?: boolean;
}

interface DashboardStats {
  totalClicks: number;
  totalConversions: number;
  conversionRate: number;
  totalPayout: number;
  todayClicks: number;
  todayConversions: number;
}

interface RealtimeClick {
  user: string;
  country: string;
  city: string;
  region: string;
  ip_address: string;
  user_agent: string;
  network: string;
  traffic_source?: string;
  traffic_type?: string;
  device_type?: string;
  created_at: string;
  timestamp?: number;
}

const Dashboard: Component = () => {
  const [todayConversions, setTodayConversions] = createSignal<ConversionWithAnimation[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [stats, setStats] = createSignal<DashboardStats>({
    totalClicks: 0,
    totalConversions: 0,
    conversionRate: 0,
    totalPayout: 0,
    todayClicks: 0,
    todayConversions: 0,
  });
  const [statsLoading, setStatsLoading] = createSignal(true);
  const [selectedClick, setSelectedClick] = createSignal<RealtimeClick | null>(null);
  const [sevenDayChartData, setSevenDayChartData] = createSignal<{
    labels: string[];
    datasets: { label: string; data: number[] }[];
  } | null>(null);
  const [sevenDayLoading, setSevenDayLoading] = createSignal(true);
  const [sevenDayUpdating, setSevenDayUpdating] = createSignal(false);
  const [realtimeCount, setRealtimeCount] = createSignal(0);
  const [lastConversionTime, setLastConversionTime] = createSignal<string>('');
  const [searchInput, setSearchInput] = createSignal('');

  // 7-Day Performance Overview - Minimized state with localStorage persistence
  const [sevenDayMinimized, setSevenDayMinimized] = createSignal(
    localStorage.getItem('dashboard-sevenday-minimized') === 'true'
  );

  // Filtered conversions based on search input (supports comma-separated OR filter)
  const filteredConversions = createMemo(() => {
    const search = searchInput().toLowerCase().trim();
    if (!search) return todayConversions();

    // Split by comma and filter empty terms - supports "term1,term2,term3" format
    const searchTerms = search.split(',').map(term => term.trim()).filter(Boolean);
    if (searchTerms.length === 0) return todayConversions();

    // Filter: each row must match at least ONE search term (OR logic)
    return todayConversions().filter(conv =>
      searchTerms.some(term =>
        conv.user?.toLowerCase().includes(term) ||
        conv.country?.toLowerCase().includes(term) ||
        conv.network?.toLowerCase().includes(term) ||
        conv.ip_address?.toLowerCase().includes(term)
      )
    );
  });

  // Calculate earning breakdown in a single pass (optimized performance)
  const earningBreakdown = createMemo(() => {
    const conversionsData = todayConversions();
    const totals = {
      total: 0,
      byNetwork: {} as Record<string, number>,
      byCountry: {} as Record<string, number>,
      byTraffic: {} as Record<string, number>,
      byUser: {} as Record<string, number>,
    };

    conversionsData.forEach(conv => {
      const payout = Number(conv.payout || 0);
      totals.total += payout;

      // By network
      if (!totals.byNetwork[conv.network]) totals.byNetwork[conv.network] = 0;
      totals.byNetwork[conv.network] += payout;

      // By country
      if (!totals.byCountry[conv.country]) totals.byCountry[conv.country] = 0;
      totals.byCountry[conv.country] += payout;

      // By traffic source
      const traffic = conv.traffic_source || 'Unknown';
      if (!totals.byTraffic[traffic]) totals.byTraffic[traffic] = 0;
      totals.byTraffic[traffic] += payout;

      // By user
      if (!totals.byUser[conv.user]) totals.byUser[conv.user] = 0;
      totals.byUser[conv.user] += payout;
    });

    return totals;
  });

  // Get total payout
  const getTotalPayout = () => earningBreakdown().total;

  // Get earning by network
  const getNetworkEarning = (network: string) => earningBreakdown().byNetwork[network] || 0;

  // Get earning by country
  const getCountryEarning = (country: string) => earningBreakdown().byCountry[country] || 0;

  // Get earning by traffic source
  const getTrafficEarning = (traffic: string) => earningBreakdown().byTraffic[traffic] || 0;

  // Get earning by user
  const getUserEarning = (user: string) => earningBreakdown().byUser[user] || 0;

  // Get icon based on traffic source (where traffic came from)
  const getTrafficSourceIcon = (conversion: Conversion) => {
    return (
      <PlatformIcon
        platform={conversion.traffic_source}
        size={16}
        class={`${getPlatformColor(conversion.traffic_source)}`}
      />
    );
  };

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const data = await statsApi.getDashboard();
      setStats(data);
    } catch (error) {
      // Error loading stats
    } finally {
      setStatsLoading(false);
    }
  };

  const loadTodayConversions = async () => {
    setLoading(true);
    try {
      const today = getTodayGMT();
      const data = await conversionsApi.getAll({
        startDate: today,
        endDate: today,
      });
      setTodayConversions(data.data || []);
    } catch (error) {
      setTodayConversions([]);
    } finally {
      setLoading(false);
    }
  };

  // Get total payout from optimized breakdown
  const totalPayout = () => getTotalPayout();

  // Calculate top 1 earner
  const topEarner = createMemo(() => {
    const userEarnings = earningBreakdown().byUser;
    if (Object.keys(userEarnings).length === 0) return null;

    const topEntry = Object.entries(userEarnings).sort(([, a], [, b]) => b - a)[0];
    return topEntry?.[0] || null;
  });

  // Check if user is top 1 earner
  const isTopEarner = (user: string): boolean => {
    return topEarner() === user;
  };

  // Use shared conversions columns utility with render functions
  const columns: Column<Conversion>[] = getConversionsColumns({
    isTopEarner,
    renderGMTCell: (conversion) => (
      <span class="text-sm">{formatGMTTime(conversion.datetime)}</span>
    ),
    renderWIBCell: (conversion) => (
      <span class="text-sm">{formatWIBTime(conversion.datetime)}</span>
    ),
    renderUserCell: (conversion) => {
      if (isTopEarner(conversion.user)) {
        return (
          <div class="flex items-center gap-2">
            <span class="font-bold text-gray-900 dark:text-white">{conversion.user}</span>
            <OcFeedtrophy2 size={16} class="text-amber-400" />
          </div>
        );
      }
      return <span class="font-mono text-sm">{conversion.user}</span>;
    },
    renderAccessCell: (conversion) => (
      <div class="flex gap-1 items-center justify-center">
        <CountryFlag code={conversion.country || 'xx'} size={16} />
        {getTrafficSourceIcon(conversion)}
        <NetworkLogo network={conversion.network} size={16} />
      </div>
    ),
    renderNetworkCell: (conversion) => null, // Merged into Access cell
    renderEarningCell: (conversion) => (
      <div class="flex items-center justify-end gap-1">
        <span class="text-sm">${Number(conversion.payout).toFixed(2)}</span>
        <TbCoins size={16} class="text-amber-400" />
      </div>
    ),
    renderIPCell: (conversion) => (
      <span class="font-mono text-xs text-gray-600 dark:text-gray-400">
        {conversion.ip_address || '-'}
      </span>
    ),
  });

  // Get row class for new row animation
  const getDashboardRowClass = (conversion: ConversionWithAnimation): string => {
    if (conversion._isNew) {
      return 'new-row-animate new-row-highlight';
    }
    return '';
  };

  // Debounce chart updates to prevent excessive API calls
  let chartUpdateTimeout: ReturnType<typeof setTimeout> | null = null;

  const loadSevenDayChart = async (isRealtimeUpdate = false) => {
    // Debounce real-time updates (500ms delay to batch multiple clicks)
    if (isRealtimeUpdate && chartUpdateTimeout) {
      clearTimeout(chartUpdateTimeout);
    }

    const performUpdate = async () => {
      // Use different loading states for initial load vs real-time updates
      if (isRealtimeUpdate) {
        setSevenDayUpdating(true);
      } else {
        setSevenDayLoading(true);
      }

      try {
        const { startDate, endDate } = getDateRangeGMT(7);

        // Load both clicks and conversions data in parallel
        const [clicksData, conversionsData] = await Promise.all([
          statsApi.getClicksChart('day', startDate, endDate),
          statsApi.getConversionsChart('day', startDate, endDate)
        ]);

        // Merge the two datasets
        const mergedData = {
          labels: clicksData.labels || conversionsData.labels,
          datasets: [
            // Clicks dataset
            {
              label: 'Clicks',
              data: clicksData.datasets?.[0]?.data || [],
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              tension: 0.4,
              fill: true,
              yAxisID: 'y'
            },
            // Conversions dataset
            {
              label: 'Conversions',
              data: conversionsData.datasets?.[0]?.data || [],
              borderColor: '#10b981',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              tension: 0.4,
              fill: true,
              yAxisID: 'y1'
            },
            // Earnings dataset (if available)
            ...(conversionsData.datasets?.[1] ? [{
              label: 'Total Earnings ($)',
              data: conversionsData.datasets[1].data || [],
              borderColor: '#f59e0b',
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              tension: 0.4,
              fill: true,
              yAxisID: 'y2'
            }] : [])
          ]
        };

        setSevenDayChartData(mergedData);
      } catch (error) {
        setSevenDayChartData(null);
      } finally {
        if (isRealtimeUpdate) {
          setSevenDayUpdating(false);
        } else {
          setSevenDayLoading(false);
        }
      }
    };

    // For real-time updates, debounce with 500ms delay to batch multiple clicks
    if (isRealtimeUpdate) {
      chartUpdateTimeout = setTimeout(performUpdate, 500);
    } else {
      // For initial load, execute immediately
      await performUpdate();
    }
  };

  // Debounce timer for stats refresh
  let statsRefreshTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Optimistic update for real-time clicks
   * Updates stats locally without API call for instant UI feedback
   * Fetches fresh data periodically (debounced every 5 minutes for responsiveness)
   */
  const optimisticAddClick = () => {
    // Update stats locally (optimistic update)
    // Socket clicks go to memory first, so increment memory totals
    setStats((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        // Increment memory clicks (unsaved in aggregator)
        memoryClicks: (prev.memoryClicks || 0) + 1,
        memoryTodayClicks: (prev.memoryTodayClicks || 0) + 1,
        // Increment combined totals (what's displayed on stats card)
        totalClicksWithMemory: (prev.totalClicksWithMemory || 0) + 1,
        todayClicksWithMemory: (prev.todayClicksWithMemory || 0) + 1,
        // Keep database values unchanged (they sync via flush)
        totalClicks: prev.totalClicks,
        todayClicks: prev.todayClicks
      };
    });

    // Debounce the full stats refresh to every 5 minutes for more responsive updates
    if (statsRefreshTimeout) {
      clearTimeout(statsRefreshTimeout);
    }

    statsRefreshTimeout = setTimeout(() => {
      loadStats();
    }, 300000);
  };

  /**
   * Optimistic update for conversions
   * Similar to clicks, updates locally and syncs periodically (every 5 minutes)
   */
  const optimisticAddConversion = (payout: number = 0) => {
    // Update stats locally
    setStats((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        totalConversions: (prev.totalConversions || 0) + 1,
        totalPayout: (prev.totalPayout || 0) + payout
      };
    });

    // Debounce the full stats refresh to every 5 minutes for more responsive updates
    if (statsRefreshTimeout) {
      clearTimeout(statsRefreshTimeout);
    }

    statsRefreshTimeout = setTimeout(() => {
      loadStats();
    }, 300000);
  };

  let sevenDayChartInterval: ReturnType<typeof setInterval> | null = null;

  onMount(async () => {
    // Set page title
    setDocumentTitle('Dashboard');

    // Load stats, today's conversions, and 7-day chart
    loadStats();
    loadTodayConversions();
    loadSevenDayChart(false); // Initial load

    // Set up 15-minute polling for 7-day chart (900,000ms = 15 minutes)
    // Chart no longer updates on every click/conversion for better performance
    sevenDayChartInterval = setInterval(() => {
      loadSevenDayChart(false);
    }, 900000);

    // Note: Socket listeners for clicks are now registered globally in Layout component
    // to prevent race conditions and duplicate handlers during page navigation
    // The Layout component registers them once and updates the global realtimeClicksStore
    // Dashboard can still use optimistic updates for stats if needed

    // Listen for real-time click events to update stats cards
    useSocketEvent<RealtimeClick>('click-recorded', () => {
      optimisticAddClick();
    });

    // Listen for real-time conversion events
    // Toast notification is handled globally by Layout component
    useSocketEvent<Conversion>('conversion-recorded', (data) => {
      // Add new conversion to the beginning of the list with deduplication
      setTodayConversions((prevConversions) => {
        const newConversion: ConversionWithAnimation = {
          ...data,
          datetime: data.datetime || new Date().toISOString(),
          _isNew: true,
        };

        // Only show if conversion is from today (GMT+0)
        if (!isTodayGMT(newConversion.datetime)) {
          return prevConversions;
        }

        // Check for duplicates by ID or unique combination
        const isDuplicate = prevConversions.some(conv => {
          // If both have IDs, compare by ID
          if (conv.id && newConversion.id) {
            return conv.id === newConversion.id;
          }
          // Otherwise compare by user + datetime + payout (unique enough)
          return conv.user === newConversion.user &&
            conv.datetime === newConversion.datetime &&
            conv.payout === newConversion.payout;
        });

        if (isDuplicate) {
          return prevConversions;
        }

        // Increment realtime counter
        setRealtimeCount((c) => c + 1);
        setLastConversionTime(newConversion.datetime);

        // Optimistic update: increment conversion counters immediately
        optimisticAddConversion(newConversion.payout || 0);

        // Keep only recent 1000 items in memory
        const updated = [newConversion, ...prevConversions].slice(0, 1000);

        // Remove _isNew flag after animation completes (0.4s for slide-in + 2s for highlight = 2.4s)
        setTimeout(() => {
          setTodayConversions((current) =>
            current.map((conversion) =>
              conversion._isNew ? { ...conversion, _isNew: false } : conversion
            )
          );
        }, 2400);

        return updated;
      });
    });

    // Clean up interval on component unmount
    onCleanup(() => {
      if (sevenDayChartInterval) {
        clearInterval(sevenDayChartInterval);
      }
    });
  });

  return (
    <div class="space-y-6">
      {/* Stats Cards */}
      <div class="relative">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatsCard
            title="Today's Clicks"
            value={stats().todayClicksWithMemory?.toString() || stats().todayClicks.toString()}
            icon={() => <TbClick size={24} />}
            color="blue"
            loading={statsLoading()}
          />
          <StatsCard
            title="Conversions"
            value={todayConversions().length.toString()}
            icon={() => <TbCheckbox size={24} />}
            color="green"
            loading={loading()}
          />
          <StatsCard
            title="Earning"
            value={`$${totalPayout().toFixed(2)}`}
            icon={() => <TbCoins size={24} />}
            color="yellow"
            loading={loading()}
          />

        </div>
      </div>

      {/* 7-Day Conversions Chart */}
      <div class={`hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow transition-all duration-300 ${sevenDayMinimized() ? 'p-2 opacity-50' : 'p-6'}`}>
        <div class={`flex items-center justify-between ${sevenDayMinimized() ? 'px-1' : 'mb-4'}`}>
          <h2 class={`font-semibold flex items-center gap-2 ${sevenDayMinimized() ? 'text-xs text-gray-500 dark:text-gray-500' : 'text-lg text-gray-900 dark:text-white'}`}>
            <TbChartLine size={sevenDayMinimized() ? 16 : 20} class={sevenDayMinimized() ? 'text-gray-500 dark:text-gray-500' : ''} />
            <span class={sevenDayMinimized() ? 'hidden' : ''}>Last 7 Days - Performance Overview</span>
            {sevenDayUpdating() && (
              <span class="inline-flex items-center gap-1 ml-2">
                <span class="inline-block w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                <span class="text-xs text-orange-500">Updating...</span>
              </span>
            )}
          </h2>
          <button
            onClick={() => {
              const newState = !sevenDayMinimized();
              setSevenDayMinimized(newState);
              localStorage.setItem('dashboard-sevenday-minimized', String(newState));
            }}
            class="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition text-gray-500 dark:text-gray-400 flex-shrink-0"
            title={sevenDayMinimized() ? 'Expand' : 'Collapse'}
          >
            {sevenDayMinimized() ? <TbPlus size={20} /> : <TbMinus size={20} />}
          </button>
        </div>
        <Show when={!sevenDayMinimized()}>
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Clicks, conversions, and earnings trends</p>
            <div class="mt-4">
              {sevenDayLoading() ? (
                <div class="flex items-center justify-center h-80">
                  <p class="text-gray-500 dark:text-gray-400">Loading chart...</p>
                </div>
              ) : sevenDayChartData() ? (
                <LineChart
                  labels={sevenDayChartData()!.labels}
                  datasets={sevenDayChartData()!.datasets}
                  height={350}
                />
              ) : (
                <div class="flex items-center justify-center h-80">
                  <p class="text-gray-500 dark:text-gray-400">No data available</p>
                </div>
              )}
            </div>
          </div>
        </Show>
      </div>

      {/* Today's Conversions Table */}
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <TbCalendar size={20} />
            Today's Conversions
          </h2>
          {/* Filtered Earning - Right Side */}
          <Show when={searchInput().trim().length > 0 && filteredConversions().length > 0}>
            <div class="text-right">
              <p class="hidden md:block text-xs text-gray-500 dark:text-gray-400">Filtered Earning</p>
              <p class="text-lg font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1 justify-end">
                <span class="md:hidden text-xs text-gray-500 dark:text-gray-400 mr-1">Filtered:</span>
                <TbCoins size={16} />
                ${filteredConversions().reduce((sum, conv) => sum + Number(conv.payout || 0), 0).toFixed(2)}
              </p>
            </div>
          </Show>

        </div>

        <SearchInput
          value={searchInput()}
          onInput={(e) => setSearchInput(e)}
          placeholder="Filter by User, Country, Network, or IP..."
        />

        <div class="overflow-x-auto">
          <DataTable
            data={filteredConversions()}
            columns={columns}
            loading={loading()}
            searchable={false}
            defaultSortKey="created_at"
            defaultSortOrder="desc"
            getRowClass={getDashboardRowClass}
          />
        </div>
      </div>

      {/* Click Details Modal */}
      {selectedClick() && (
        <div class="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50">
          <div class="w-full bg-white dark:bg-gray-800 rounded-t-lg shadow-lg p-6 max-h-96 overflow-y-auto">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Click Details</h3>
              <button
                onClick={() => setSelectedClick(null)}
                class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            {selectedClick() && (
              <div class="space-y-3">
                {/* User ID */}
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">User</p>
                  <p class="font-mono text-sm text-gray-900 dark:text-gray-100">{selectedClick()!.user}</p>
                </div>

                {/* Country */}
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">Country</p>
                  <p class="text-sm text-gray-900 dark:text-gray-100">{selectedClick()!.country}</p>
                </div>

                {/* Traffic Source */}
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">Traffic Source</p>
                  <span class="text-sm bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-3 py-1 rounded inline-block">
                    {selectedClick()!.traffic_source || 'Others'}
                  </span>
                </div>

                {/* Location */}
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">Location</p>
                  <p class="text-sm text-gray-900 dark:text-gray-100">
                    {selectedClick()!.city}{selectedClick()!.region ? `, ${selectedClick()!.region}` : ''}, {selectedClick()!.country}
                  </p>
                </div>

                {/* IP Address */}
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">IP Address</p>
                  <p class="font-mono text-sm text-gray-900 dark:text-gray-100">{selectedClick()!.ip_address}</p>
                </div>

                {/* Network */}
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">Network</p>
                  <p class="text-sm text-gray-900 dark:text-gray-100 capitalize">{selectedClick()!.network || 'direct'}</p>
                </div>

                {/* Access Type */}
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">Access Type</p>
                  <p class="text-sm text-gray-900 dark:text-gray-100 capitalize">{selectedClick()!.traffic_type || 'organic'}</p>
                </div>

                {/* Device */}
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">Device</p>
                  <p class="text-sm text-gray-900 dark:text-gray-100">
                    {selectedClick()!.device_type === 'mobile' ? '📱 Mobile' : '🖥️ Desktop'}
                  </p>
                </div>

                {/* Timestamp */}
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">Timestamp</p>
                  <p class="text-sm text-gray-900 dark:text-gray-100">{moment(selectedClick()!.created_at).format('YYYY-MM-DD HH:mm:ss')}</p>
                </div>

                {/* User Agent */}
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">User Agent</p>
                  <p class="text-xs text-gray-900 dark:text-gray-100 break-all">{selectedClick()!.user_agent}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
